import React, { forwardRef, useState, useEffect } from "react";
import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { DateTime } from "luxon";
import {
  SHLinkAccessLogDto,
  SHLinkDescriptionDto,
} from "@docket/consumer-app-client-typescript-axios";
import { Capacitor } from "@capacitor/core";
import { Dialog } from "@capacitor/dialog";
import {
  loadableCompleteVerifiedRecordsAtom,
  izProviderConfigsAtom,
  isHydrated,
  shLinkPasscodeAtomFamily,
} from "../../jotai/atoms";
import {
  updateSmartHealthLinkAtoms,
  setSmartHealthLinkPasscode,
  deleteSmartHealthLinkPasscode,
} from "../../jotai/atomUpdater";
import { SmartHealthLinkSchema } from "../../utils/formUtils";
import { errorLog } from "../../utils/logger";
import { DocketAPIError, getAPIClient } from "../../apiClient";
import { getDateTime } from "../../utils/data";
import { Squirrel } from "../../components/spinner/Squirrel";
import { ErrorModal } from "../../components/modals/ErrorModal";
import { Modal } from "../../components/modals/Modal";
import ResponsiveDatePicker from "../../components/form/ResponsiveDatePicker";
import {
  ModalV2,
  ModalV2BackButton,
  ModalV2Body,
  ModalV2Header,
} from "../../components/modals/ModalV2";
import { ImageIcon } from "../../components/ImageIcon";

type ModalView = "main" | "logs";

interface SmartHealthLinkModalProps {
  recordId: string | null;
}

export const SmartHealthLinkModal = forwardRef<HTMLDialogElement, SmartHealthLinkModalProps>(
  function SmartHealthLinkModal({ recordId }, ref) {
    const { t } = useTranslation();
    const [allVerifiedRecords] = useAtom(loadableCompleteVerifiedRecordsAtom);
    const [providerConfigs] = useAtom(izProviderConfigsAtom);
    const [currentView, setCurrentView] = useState<ModalView>("main");

    const currRecord = React.useMemo(() => {
      if (
        !recordId ||
        allVerifiedRecords.state !== "hasData" ||
        !isHydrated(allVerifiedRecords.data)
      ) {
        return null;
      }
      return allVerifiedRecords.data.find((record) => record.record.uid === recordId);
    }, [allVerifiedRecords, recordId]);

    const providerConfig = isHydrated(providerConfigs)
      ? providerConfigs.find(
          (config) =>
            config.iz_provider.destination_id.toLowerCase() ===
            currRecord?.record?.izProviderKey.toLowerCase()
        )
      : undefined;

    // Reset view when recordId changes
    useEffect(() => {
      if (recordId) {
        setCurrentView("main");
      }
    }, [recordId]);

    const getTitle = () => {
      if (currentView === "logs") {
        return t("immunizations.shlink_access_log_title");
      }
      {
        /* TODO */
      }
      return "SMART® Health Link";
    };

    const renderContent = () => {
      if (allVerifiedRecords.state === "loading") {
        return (
          <div className="h-full items-center justify-center flex">
            <Squirrel />
          </div>
        );
      }

      if (!recordId || !currRecord?.record.uid || !providerConfig?.features.smart_health_links) {
        return (
          <div className="p-4 text-center">
            {/* TODO */}
            <p>Record not Found</p>
          </div>
        );
      }

      if (currentView === "logs" && currRecord.record.shLink) {
        return <LinkLogsView shLink={currRecord.record.shLink} />;
      }

      return currRecord?.record.shLink ? (
        <ExistingLinkView
          link={currRecord.record.shLink}
          patient={`${currRecord.record.firstName} ${currRecord.record.lastName}`}
          onViewLogs={() => setCurrentView("logs")}
          onDeactivate={() => {}}
        />
      ) : (
        <AddLinkView recordId={currRecord!.record.uid} onSuccess={() => {}} />
      );
    };

    return (
      <>
        <ModalV2 modalBoxClassName="bg-base-100 max-w-xl" ref={ref}>
          <div>
            <ModalV2Header className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2">
                {currentView !== "logs" && <ModalV2BackButton />}
                {currentView === "logs" && (
                  <button
                    className="btn btn-primary btn-ghost text-primary-content"
                    onClick={() => setCurrentView("main")}
                  >
                    <ImageIcon
                      className="w-[calc(var(--size)*0.5)] h-[calc(var(--size)*0.5)]"
                      src="/assets/images/chevron_left_white_square.png"
                    />
                  </button>
                )}
                <p className="font-semibold text-xl">{getTitle()}</p>
              </div>
            </ModalV2Header>
          </div>
          <ModalV2Body className="bg-base-100 pt-2">{renderContent()}</ModalV2Body>
        </ModalV2>
      </>
    );
  }
);

// AddLink view component - exported for testing
export function AddLinkView({ recordId, onSuccess }: { recordId: string; onSuccess: () => void }) {
  const { t } = useTranslation();
  const [showErrorModal, setShowErrorModal] = useState<string>();

  const {
    register,
    setValue,
    watch,
    formState: { errors },
    handleSubmit,
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(SmartHealthLinkSchema),
    defaultValues: {
      expirationDate: DateTime.now().plus({ days: 1 }).toJSDate(),
      passcode: "",
      izHistoryUid: recordId,
    },
  });

  const onSubmit = async (data: any) => {
    try {
      const newLink = await getAPIClient().postSmartHealthLink({
        izHistoryUid: data.izHistoryUid,
        expirationDate: data.expirationDate,
        passcode: data.passcode,
      });
      await updateSmartHealthLinkAtoms();
      await setSmartHealthLinkPasscode(newLink.id.toString(), data.passcode);
      onSuccess();
    } catch (e: unknown) {
      errorLog(e as Error);
      if (e instanceof DocketAPIError) {
        setShowErrorModal(e.message);
      }
    }
  };

  return (
    <div className="p-4">
      {showErrorModal && (
        <ErrorModal title={t("generic.error")} onCloseModal={() => setShowErrorModal(undefined)}>
          <p>{showErrorModal}</p>
        </ErrorModal>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <p className="text-2xl font-semibold">{t("immunizations.shlink_new_title")}</p>
            <p className="text-md">{t("immunizations.shlink_new_message")}</p>
          </div>
          <div>
            <label className="text-[16px] font-semibold">Expiration Date</label>
            <div>
              <ResponsiveDatePicker
                key="expirationDate"
                selectedDate={watch("expirationDate") ?? null}
                setSelectedDate={(d) =>
                  setValue("expirationDate", d ?? DateTime.now().plus({ days: 1 }).toJSDate())
                }
                id="expirationDate"
              />
            </div>
            {errors.expirationDate && (
              <p className="text-red-500 text-xs">{errors.expirationDate.message}</p>
            )}
          </div>
          <div className="flex flex-col">
            <p className="text-[16px] font-[700]">Passcode</p>
            <label className="input w-full">
              <svg className="h-[1em]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <g
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  strokeWidth="2.5"
                  fill="none"
                  stroke="currentColor"
                >
                  <path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z"></path>
                  <circle cx="16.5" cy="7.5" r=".5" fill="currentColor"></circle>
                </g>
              </svg>
              <input
                data-testid="passcode"
                className="!border-none text-base"
                type="password"
                required
                minLength={4}
                maxLength={8}
                {...register("passcode")}
              />
            </label>
            {errors.passcode && (
              <p className="text-red-500 text-xs mt-1">{errors.passcode.message}</p>
            )}
          </div>
          <div className="flex flex-col">
            <p className="text-xs">{t("immunizations.shlink_new_legal")}</p>
            <a
              className="text-xs underline text-blue-500"
              target="_blank"
              rel="noopener noreferrer"
              href={t("immunizations.shlink_url")}
            >
              {t("immunizations.shlink_url")}
            </a>
          </div>
          <button data-testid="activate-button" className="btn w-full" type="submit">
            {t("immunizations.shlink_new_button")}
          </button>
        </div>
      </form>
    </div>
  );
}

// ExistingLink view component - exported for testing
export function ExistingLinkView({
  link,
  patient,
  onViewLogs,
  onDeactivate,
}: {
  link: SHLinkDescriptionDto;
  patient: string;
  onViewLogs: () => void;
  onDeactivate: () => void;
}) {
  const { t } = useTranslation();
  const [showDeactivate, setShowDeactivate] = useState(false);
  const [showCopied, setShowCopied] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState<string>();
  const [showPasscode, setShowPasscode] = useState(false);
  const [passcode] = useAtom(shLinkPasscodeAtomFamily(link.id.toString()));
  const [visiblePasscode, setVisiblePasscode] = useState<string>("******");

  const deactivate = async () => {
    try {
      await getAPIClient().deleteSmartHealthLink(link.id.toString());
      await deleteSmartHealthLinkPasscode(link.id.toString());
      await updateSmartHealthLinkAtoms();
      onDeactivate();
    } catch (e: unknown) {
      errorLog(e as Error);
      if (e instanceof DocketAPIError) {
        setShowErrorModal(e.message);
      }
    }
  };

  const onShowPasscodeClick = () => {
    if (!passcode) {
      if (Capacitor.isNativePlatform()) {
        Dialog.alert({
          title: "Passcode!",
          message:
            "Your passcode is not available - remember your passcode when accessing Docket® on a new device, or deactivate your SMART® Health Link and create a new one.",
        });
      } else {
        setShowErrorModal(
          "Your passcode is not available - remember your passcode when accessing Docket® on a new device, or deactivate your SMART® Health Link and create a new one."
        );
      }
    } else if (showPasscode) {
      setShowPasscode(false);
      setVisiblePasscode("******");
    } else {
      setVisiblePasscode(passcode.passcode);
      setShowPasscode(true);
    }
  };

  const onCopyClick = () => {
    navigator.clipboard.writeText(link.payload);
    if (Capacitor.isNativePlatform()) {
      Dialog.alert({
        title: "Copied",
        message:
          "SMART® Health Link copied to clipboard - send to your requesting party with the passcode.",
      });
    } else {
      setShowCopied(true);
    }
  };

  const onDeactivateClick = async () => {
    if (Capacitor.isNativePlatform()) {
      const result = await Dialog.confirm({
        title: "Deactivate Link?",
        message: "Are you sure you want to deactivate this SMART® Health Link?",
        okButtonTitle: "No",
        cancelButtonTitle: "Yes",
      });

      if (!result.value) {
        await deactivate();
      }
    } else {
      setShowDeactivate(true);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      {showErrorModal && (
        <ErrorModal title={t("generic.error")} onCloseModal={() => setShowErrorModal(undefined)}>
          <p>{showErrorModal}</p>
        </ErrorModal>
      )}
      {showDeactivate && (
        <Modal
          id="search-nav-modal"
          open={true}
          hasClose={true}
          title="Deactivate Link?"
          onCloseModal={() => setShowDeactivate(false)}
          actionButtons={
            <>
              <button
                className="btn"
                aria-label="Deactivate link"
                onClick={() => deactivate()}
                data-testid="deactivate-btn"
              >
                Yes
              </button>
              <button
                className="btn info"
                aria-label="Do not deactivate link"
                onClick={() => setShowDeactivate(false)}
              >
                No
              </button>
            </>
          }
        >
          <p>Are you sure you want to deactivate this SMART® Health Link?</p>
        </Modal>
      )}
      {showCopied && (
        <Modal
          id="copied-modal"
          open={true}
          hasClose={true}
          title="Copied"
          onCloseModal={() => setShowCopied(false)}
          actionButtons={
            <>
              <button
                className="btn"
                aria-label="OK"
                onClick={() => setShowCopied(false)}
                data-testid="ok-btn"
              >
                {t("generic.ok")}
              </button>
            </>
          }
        >
          <p>
            SMART® Health Link copied to clipboard - send to your requesting party with the
            passcode.
          </p>
        </Modal>
      )}
      <fieldset className="fieldset">
        <legend className="fieldset-legend text-lg">
          {t("immunizations.shlink_existing_title")}
        </legend>
        <input type="text" value={link.payload} className="input input-primary w-full" readOnly />
        <p className="label italic text-sm">
          Expires {getDateTime(link.expirationDate).toFormat("MM/dd/yyyy")}
        </p>
      </fieldset>
      <div className="flex gap-2 items-center">
        <p className="text-lg">Passcode:</p>
        <p data-show={showPasscode} className="data-[show=false]:pt-1.5 data-[show=true]:pt-0.5">
          {visiblePasscode}
        </p>
        <div
          className="ml-auto btn btn-primary"
          onClick={onShowPasscodeClick}
          data-testid="show-passcode"
        >
          <img
            width="20px"
            className="mx-auto my-2"
            src={showPasscode ? "/assets/images/eye.svg" : "/assets/images/eye-slash.svg"}
            alt={showPasscode ? "Hide passcode" : "Show passcode"}
          />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <p className="text-lg">Share</p>
        <button className="btn w-full btn-lg" onClick={onCopyClick}>
          {t("immunizations.shlink_existing_copy_button")}
        </button>
      </div>
      <div className="flex flex-col gap-2">
        <p className="text-lg">Manage</p>
        <div className="flex flex-col gap-2">
          <button className="btn w-full btn-lg" onClick={onViewLogs}>
            {t("immunizations.shlink_existing_access_log_button")}
          </button>
          <button
            className="btn w-full text-no-match btn-lg"
            onClick={onDeactivateClick}
            data-testid="confirm-deactivate-btn"
          >
            {t("immunizations.shlink_existing_deactivate_button")}
          </button>
        </div>
      </div>
    </div>
  );
}

// LinkLogs view component - exported for testing
export function LinkLogsView({ shLink }: { shLink: SHLinkDescriptionDto }) {
  const { t } = useTranslation();
  const [initialized, setInitialized] = useState(false);
  const [accessLogs, setAccessLogs] = useState<SHLinkAccessLogDto[]>([]);

  useEffect(() => {
    async function fetchAccessLogs() {
      const logs = await getAPIClient().getSmartHealthLinkAccessLogs(shLink.id.toString());
      setAccessLogs(logs);
      setInitialized(true);
    }

    fetchAccessLogs();
  }, [shLink.id]);

  if (!initialized) {
    return (
      <div className="h-full items-center justify-center flex">
        <Squirrel />
      </div>
    );
  }

  return (
    <div className="flex flex-col p-4 gap-4">
      {accessLogs.length === 0 ? (
        <p className="text-md">
          You have not shared this SMART® Health Link with anyone, or no one has accessed it yet.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {accessLogs.map((log, i) => (
            <div
              key={i}
              data-testid={`access-log-${i}`}
              className="py-4 px-2 bg-access-blue rounded-md flex flex-col gap-0.5 shad-primary shadow-sm"
            >
              <div className="flex gap-2">
                <p className="text-md">Date Accessed: </p>
                <p className="text-sm pt-0.5">{getDateTime(log.accessedAt).toLocaleString()}</p>
              </div>
              <div className="flex gap-2">
                <p className="text-md">Time Accessed: </p>
                <p className="text-sm pt-0.5">{getDateTime(log.accessedAt).toFormat("hh:mm:ss")}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SmartHealthLinkModal;
